// src/App.js
import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useOutletContext
} from 'react-router-dom';
import { auth, db } from './firebase';
import { signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import Calendar from './components/crossword/Calendar';
import GridEditor from './components/crossword/GridEditor';
import WordleCalendar from './components/WordleCalendar';
import TeralehtiEditor from './components/TeralehtiEditor';
import NonoEditor from './components/nono/NonoEditor';
import NotificationComposer from './components/NotificationComposer';
import CustomIconsManager from './components/custom_icons/CustomIconsManager';
import IconCluesManager from './components/IconCluesManager';
import EmailLinkLanding from './components/EmailLinkLanding';
import BackupPanel from './components/Manage';
import ManageMenu from './components/ManageMenu';
import MobileMenu from './components/MobileMenu';
import SubTabsLayout from './components/SubTabsLayout';
import UsersPanel from './components/UsersPanel';
import ProfitPanel from './components/analytics/ProfitPanel';
import EngagementLayout, {
  EngagementOverview,
  EngagementGames,
  EngagementPuzzles,
  EngagementGrowth,
  EngagementDevices
} from './components/analytics/EngagementPanel';
import {
  fetchCrosswordPuzzles,
  fetchCrosswordPuzzle,
  createCrosswordPuzzle,
  saveCrosswordPuzzle,
  deleteCrosswordPuzzle
} from './services/crosswordService';
import { fetchWordlePuzzles as fetchWordlePuzzlesService, saveWordlePuzzle, fetchQuordlePuzzles, saveQuordlePuzzle } from './services/wordleService';
import './styles/global.css';
import './styles/calendar.css';
import './styles/editor.css';
import './styles/wordle.css';
import './styles/teralehti.css';
import './styles/nono.css';

const GridGenerator = lazy(() => import('./components/crossword/GridGenerator'));

const PUZZLE_TABS = [
  { path: 'crossword', label: 'Crossword' },
  { path: 'wordle', label: 'Wordle' },
  { path: 'quordle', label: 'Quordle' },
  { path: 'teralehti', label: 'Terälehti' },
  { path: 'nono', label: 'Nono' },
];

const SECONDARY_TABS = [
  { path: 'icons', label: 'Icons' },
  { path: 'users', label: 'Users' },
  { path: 'profit', label: 'Profit' },
  { path: 'engagement', label: 'Engagement' },
];

const ICON_TABS = [
  { to: '/admin/icons', label: 'Custom Icons', end: true },
  { to: '/admin/icons/clues', label: 'Icon Clues' },
];

const MANAGE_ITEMS = [
  { to: '/admin/manage/notifications', label: 'Notifications' },
  { to: '/admin/manage/backup', label: 'Backup' },
];

const tabClass = (base) => ({ isActive }) => `${base} ${isActive ? 'active' : ''}`;

function AdminPanel() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [puzzles, setPuzzles] = useState([]);
  const [wordlePuzzles, setWordlePuzzles] = useState([]);
  const [quordlePuzzles, setQuordlePuzzles] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const tabsRef = useRef(null);
  const { pathname } = useLocation();

  // On narrow screens the tab strip scrolls sideways: keep the current tab in view.
  useEffect(() => {
    tabsRef.current?.querySelector('.tab-button.active')?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [pathname, isAdmin]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      if (user) {
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        const adminExists = adminDoc.exists();
        setIsAdmin(adminExists);
        if (adminExists) {
          fetchPuzzles();
          fetchWordlePuzzles();
          fetchQuordlePuzzlesData();
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const handleSignOut = () => {
    signOut(auth);
    setPuzzles([]);
    setWordlePuzzles([]);
    setQuordlePuzzles([]);
  };

  const fetchPuzzles = async () => {
    try {
      const list = await fetchCrosswordPuzzles();
      setPuzzles(list);
    } catch (error) {
      console.error('Failed to fetch puzzles:', error);
    }
  };

  const fetchWordlePuzzles = async () => {
    try {
      const list = await fetchWordlePuzzlesService();
      setWordlePuzzles(list);
    } catch (error) {
      console.error('Failed to fetch Wordle puzzles:', error);
    }
  };

  const fetchQuordlePuzzlesData = async () => {
    try {
      const list = await fetchQuordlePuzzles();
      setQuordlePuzzles(list);
    } catch (error) {
      console.error('Failed to fetch Quordle puzzles:', error);
    }
  };

  const handleSaveWordlePuzzle = async (puzzleData) => {
    try {
      await saveWordlePuzzle(puzzleData);
      await fetchWordlePuzzles();
    } catch (error) {
      console.error('Failed to save Wordle puzzle:', error);
    }
  };

  const handleSaveQuordlePuzzle = async (puzzleData) => {
    try {
      await saveQuordlePuzzle(puzzleData);
      await fetchQuordlePuzzlesData();
    } catch (error) {
      console.error('Failed to save Quordle puzzle:', error);
    }
  };

  if (loading) return <div className="container">Loading...</div>;

  if (!user) {
    return (
      <div className="container">
        <h1>Norppa Admin Panel</h1>
        <button onClick={signInWithGoogle} className="btn-primary">
          Sign in with Google
        </button>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container">
        <h1>Access Denied</h1>
        <p>You don't have admin permissions.</p>
        <p>User: {user.email}</p>
        <button onClick={handleSignOut} className="btn-secondary">Sign Out</button>
      </div>
    );
  }

  const outletContext = {
    puzzles,
    fetchPuzzles,
    calendarMonth,
    setCalendarMonth,
    wordlePuzzles,
    quordlePuzzles,
    handleSaveWordlePuzzle,
    handleSaveQuordlePuzzle,
  };

  // The grid editor needs every pixel: its top bar scrolls away instead of staying pinned.
  const inEditor = /^\/admin\/crossword\/[^/]+/.test(pathname);

  return (
    <div className={`container${inEditor ? ' header-free' : ''}`}>
      <header className="header">
        <h1>Norppa Admin</h1>
        <nav className="tabs" ref={tabsRef}>
          {PUZZLE_TABS.map(tab => (
            <NavLink key={tab.path} to={`/admin/${tab.path}`} className={tabClass('tab-button')}>
              {tab.label}
            </NavLink>
          ))}
          <span className="tabs-spacer" />
          {SECONDARY_TABS.map(tab => (
            <NavLink key={tab.path} to={`/admin/${tab.path}`} className={tabClass('tab-button tab-secondary')}>
              {tab.label}
            </NavLink>
          ))}
        </nav>
        <ManageMenu items={MANAGE_ITEMS} email={user.email} onSignOut={handleSignOut} />
        <MobileMenu
          groups={[
            PUZZLE_TABS.map(t => ({ to: `/admin/${t.path}`, label: t.label })),
            SECONDARY_TABS.map(t => ({ to: `/admin/${t.path}`, label: t.label })),
            MANAGE_ITEMS,
          ]}
          email={user.email}
          onSignOut={handleSignOut}
        />
      </header>

      <main className="main">
        <Outlet context={outletContext} />
      </main>
    </div>
  );
}

function CrosswordCalendarPage() {
  const { puzzles, calendarMonth, setCalendarMonth } = useOutletContext();
  const navigate = useNavigate();

  return (
    <Calendar
      puzzles={puzzles}
      onSelectPuzzle={(p) => navigate(`/admin/crossword/${p.id}`)}
      onCreatePuzzle={(dateStr) => navigate(`/admin/crossword/${dateStr}`)}
      month={calendarMonth}
      onMonthChange={setCalendarMonth}
    />
  );
}

const DATE_ID_PATTERN = /^\d{2}-\d{2}-\d{4}$/;

function CrosswordEditorPage() {
  const { date } = useParams();
  const { puzzles, fetchPuzzles, setCalendarMonth } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [puzzle, setPuzzle] = useState(null);
  // Grid picked in the generator, for this date only. Kept here once read so clearing the
  // history state (a reload shouldn't paste it again) doesn't reset the editor.
  const [generated, setGenerated] = useState(() =>
    location.state?.generatedGrid ? { date, lines: location.state.generatedGrid } : null);
  const validDate = DATE_ID_PATTERN.test(date);

  useEffect(() => {
    if (location.state?.generatedGrid) {
      setGenerated({ date, lines: location.state.generatedGrid });
      navigate(location.pathname, { replace: true, state: null });
    } else if (generated && generated.date !== date) {
      setGenerated(null);
    }
  }, [date, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!validDate) return;
    let cancelled = false;
    // Always fetch fresh data from Firestore to avoid overwriting changes
    fetchCrosswordPuzzle(date)
      .catch(error => {
        console.error('Failed to fetch puzzle:', error);
        return null;
      })
      .then(fresh => {
        if (!cancelled) setPuzzle(fresh || createCrosswordPuzzle(date));
      });
    return () => { cancelled = true; };
  }, [date, validDate]);

  if (!validDate) return <Navigate to="/admin/crossword" replace />;
  if (!puzzle) return <div>Loading puzzle...</div>;

  const backToCalendar = () => {
    // Return to the month of the puzzle being edited
    const [day, month, year] = puzzle.id.split('-').map(Number);
    setCalendarMonth(new Date(year, month - 1, day));
    navigate('/admin/crossword');
  };

  const handleSave = async (puzzleData) => {
    try {
      await saveCrosswordPuzzle(puzzleData);
      await fetchPuzzles();
    } catch (error) {
      console.error('Failed to save puzzle:', error);
    }
  };

  const handleDelete = async (puzzleId) => {
    try {
      await deleteCrosswordPuzzle(puzzleId);
      await fetchPuzzles();
      navigate('/admin/crossword');
    } catch (error) {
      console.error('Failed to delete puzzle:', error);
    }
  };

  return (
    <GridEditor
      key={puzzle.id}
      puzzle={puzzle}
      puzzles={puzzles}
      onSave={handleSave}
      onBack={backToCalendar}
      onDelete={handleDelete}
      onNavigate={(dateStr) => {
        navigate(`/admin/crossword/${dateStr}`);
        // Keep calendar in sync
        fetchPuzzles();
      }}
      onGenerate={(grid) => navigate(`/admin/crossword/${puzzle.id}/generate`, { state: { grid } })}
      generatedGrid={generated?.date === puzzle.id ? generated.lines : null}
    />
  );
}

// Starting grid for the generator from a saved puzzle: size, black squares and letters.
const puzzleToGeneratorGrid = (puzzle) => {
  const w = puzzle?.gridSize?.width || 6, h = puzzle?.gridSize?.height || 7;
  const black = (puzzle?.blackSquares || []).map(({ row, col }) => row * w + col);
  const letters = {};
  (puzzle?.words || []).forEach(word => {
    for (let i = 0; i < word.answer.length; i++) {
      const row = word.direction === 'across' ? word.startRow : word.startRow + i;
      const col = word.direction === 'across' ? word.startCol + i : word.startCol;
      letters[row * w + col] = word.answer[i];
    }
  });
  return { w, h, black, letters };
};

function CrosswordGeneratorPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [puzzle, setPuzzle] = useState(undefined);
  const validDate = DATE_ID_PATTERN.test(date);

  useEffect(() => {
    if (!validDate) return;
    fetchCrosswordPuzzle(date).catch(() => null).then(p => setPuzzle(p || null));
  }, [date, validDate]);

  if (!validDate) return <Navigate to="/admin/crossword" replace />;
  if (puzzle === undefined) return <div>Loading puzzle...</div>;

  // The editor passes its current (possibly unsaved) grid; after a reload, use the saved one.
  const initial = location.state?.grid || puzzleToGeneratorGrid(puzzle);
  const [dd, mm] = date.split('-').map(Number);

  const use = (grid) => {
    if (puzzle?.words?.length &&
        !window.confirm(`Replace the crossword of ${dd}.${mm}.? Its current grid and clues are cleared (nothing is saved until you press Save).`)) return;
    navigate(`/admin/crossword/${date}`, { state: { generatedGrid: grid.lines } });
  };

  return (
    <Suspense fallback={<div>Loading generator...</div>}>
      <GridGenerator key={date} date={date} initial={initial} onUse={use}
        onBack={() => navigate(`/admin/crossword/${date}`)} />
    </Suspense>
  );
}

function WordlePage() {
  const { wordlePuzzles, handleSaveWordlePuzzle } = useOutletContext();
  return <WordleCalendar puzzles={wordlePuzzles} onSavePuzzle={handleSaveWordlePuzzle} />;
}

function QuordlePage() {
  const { quordlePuzzles, handleSaveQuordlePuzzle } = useOutletContext();
  return <WordleCalendar puzzles={quordlePuzzles} onSavePuzzle={handleSaveQuordlePuzzle} wordsPerDay={4} />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<EmailLinkLanding />} />
        <Route path="/admin" element={<AdminPanel />}>
          <Route index element={<Navigate to="crossword" replace />} />
          <Route path="crossword" element={<CrosswordCalendarPage />} />
          <Route path="crossword/:date" element={<CrosswordEditorPage />} />
          <Route path="crossword/:date/generate" element={<CrosswordGeneratorPage />} />
          <Route path="wordle" element={<WordlePage />} />
          <Route path="quordle" element={<QuordlePage />} />
          <Route path="teralehti" element={<TeralehtiEditor />} />
          <Route path="nono" element={<NonoEditor />} />
          <Route path="users" element={<UsersPanel />} />
          <Route path="profit" element={<ProfitPanel />} />
          <Route path="profit/ads" element={<ProfitPanel />} />
          <Route path="profit/conversion" element={<ProfitPanel />} />
          <Route path="engagement" element={<EngagementLayout />}>
            <Route index element={<EngagementOverview />} />
            <Route path="games" element={<EngagementGames />} />
            <Route path="puzzles" element={<EngagementPuzzles />} />
            <Route path="growth" element={<EngagementGrowth />} />
            <Route path="devices" element={<EngagementDevices />} />
            <Route path="*" element={<Navigate to="/admin/engagement" replace />} />
          </Route>
          <Route path="icons" element={<SubTabsLayout tabs={ICON_TABS} />}>
            <Route index element={<CustomIconsManager />} />
            <Route path="clues" element={<IconCluesManager />} />
          </Route>
          <Route path="manage">
            <Route index element={<Navigate to="notifications" replace />} />
            <Route path="notifications" element={<NotificationComposer />} />
            <Route path="backup" element={<BackupPanel />} />
          </Route>
          {/* Old paths, kept for bookmarks */}
          <Route path="notifications" element={<Navigate to="/admin/manage/notifications" replace />} />
          <Route path="icon-clues" element={<Navigate to="/admin/icons/clues" replace />} />
          <Route path="*" element={<Navigate to="crossword" replace />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
